import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import AirIcon from "@mui/icons-material/Air";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SpaIcon from "@mui/icons-material/Spa";
import {MEDITATION_TRACKS} from "./meditationTracks";

const TRACK_ICONS = {
    "pustemeditasjon-8-min": SelfImprovementIcon,
    "dype-andedrag-14-min": AirIcon,
    "selvmedfolelsesmeditasjon-20-min": FavoriteBorderIcon,
    "meditasjonsovelse-30-min": SpaIcon,
};

export const formatTrackTitle = (title) => {
    if (!title) return "";
    return title.replace(/Selvmedfølelsesmeditasjon/g, "Selvmed\u00ADfølelses\u00ADmeditasjon");
};

export const MeditationTrackSelector = ({
    tracks = MEDITATION_TRACKS,
    selectedTrackId,
    onSelectTrack,
}) => {
    return (
        <Box
            sx={{
                mb: 3,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
            }}
        >
            <Typography
                variant="subtitle2"
                component="p"
                sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontSize: "0.75rem",
                }}
            >
                Velg øvelse og varighet
            </Typography>

            <Box
                role="group"
                aria-label="Velg meditasjonsøvelse"
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))"},
                    gap: 1.5,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                }}
            >
                {tracks.map((track) => {
                    const isSelected = track.id === selectedTrackId;
                    const IconComponent = TRACK_ICONS[track.id] || SelfImprovementIcon;

                    return (
                        <Button
                            key={track.id}
                            onClick={() => onSelectTrack(track)}
                            aria-pressed={isSelected}
                            aria-label={`${track.title}, varighet ${track.durationLabel}`}
                            sx={{
                                minHeight: {xs: 96, sm: 100},
                                height: "100%",
                                p: {xs: 1.25, sm: 1.5},
                                width: "100%",
                                minWidth: 0,
                                maxWidth: "100%",
                                borderRadius: 3,
                                textTransform: "none",
                                textAlign: "left",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                justifyContent: "flex-start",
                                gap: {xs: 0.75, sm: 1},
                                overflow: "hidden",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                border: "1.5px solid",
                                borderColor: isSelected ? "primary.main" : "divider",
                                bgcolor: isSelected ? "rgba(40, 82, 56, 0.08)" : "background.paper",
                                boxShadow: isSelected
                                    ? "0 4px 12px rgba(40, 82, 56, 0.12)"
                                    : "0 1px 3px rgba(0, 0, 0, 0.02)",
                                transition: "all 0.18s ease-in-out",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    bgcolor: isSelected
                                        ? "rgba(40, 82, 56, 0.12)"
                                        : "rgba(40, 82, 56, 0.04)",
                                    transform: "translateY(-1px)",
                                },
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    minWidth: 0,
                                    mb: 0,
                                }}
                            >
                                <IconComponent
                                    sx={{
                                        fontSize: {xs: 18, sm: 20},
                                        color: isSelected ? "primary.main" : "text.secondary",
                                        flexShrink: 0,
                                    }}
                                />
                                <Chip
                                    label={track.durationLabel}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: "0.72rem",
                                        fontWeight: 700,
                                        bgcolor: isSelected ? "primary.main" : "rgba(0, 0, 0, 0.06)",
                                        color: isSelected ? "#FFFFFF" : "text.secondary",
                                        flexShrink: 0,
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isSelected ? 700 : 600,
                                    color: isSelected ? "primary.main" : "text.primary",
                                    lineHeight: 1.3,
                                    fontSize: {xs: "0.78rem", sm: "0.85rem"},
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    whiteSpace: "normal",
                                    hyphens: "auto",
                                    width: "100%",
                                    minWidth: 0,
                                    flexGrow: 1,
                                }}
                            >
                                {formatTrackTitle(track.title)}
                            </Typography>
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};
